// Director scoring hooks. Pure functions that translate a team's director into
// the modifiers the race engine applies. Kept separate from race.ts so each
// lever (pace, synergy, development ramp, luck variance, DNF) is small and
// independently testable. A null director is always a no-op (1× / +0), so a
// rival without a director or a pre-director save behaves exactly as before.
import type { Chassis, Director, Team } from "../types";

// Home-machinery boost: a director on a chassis from the constructor they ran
// historically extracts more from their specialty. Mirrors the driver's
// historicAffinity (synergy.ts) but as a single multiplier on the director's
// PERFORMANCE levers — never on the pure risk/stability traits (luck variance,
// reliability-driven DNF), which a familiar car shouldn't change.
export const DIRECTOR_AFFINITY = 1.25;
// Independents (Steiner, the generic archetypes) have no home constructor, so
// instead of the big home-only boost they carry a smaller edge that applies on
// EVERY chassis — a journeyman who gels with any garage. This keeps them
// relevant against the constructor-locked mythics on a non-matching chassis.
export const INDEPENDENT_AFFINITY = 1.1;

/**
 * The director's affinity multiplier on this chassis. A constructor director
 * gets DIRECTOR_AFFINITY on home machinery and 1× elsewhere; an independent
 * (no constructor) gets the flat INDEPENDENT_AFFINITY everywhere. 1× for no
 * director.
 */
export function directorChassisAffinity(dir: Director | null, chassis: Chassis): number {
  if (!dir) return 1;
  if (dir.team === null) return INDEPENDENT_AFFINITY;
  return dir.team === chassis.team ? DIRECTOR_AFFINITY : 1;
}

// A constructor director on the machinery they actually ran makes the CAR
// better — engineering/development know-how. Flat motor + aero added to the
// chassis (home only). Independents have no specific car they know, so they get
// none (their edge is the universal INDEPENDENT_AFFINITY on their signature).
// This is the thematically-correct home for a "knows the car" buff — the driver
// gets a familiarity edge on their own attributes (pace/consistency); the car's
// stats are the director's domain.
export const DIRECTOR_CAR_MOTOR = 4;
export const DIRECTOR_CAR_AERO = 4;

export function directorCarBonus(
  dir: Director | null,
  chassis: Chassis,
): { motor: number; aero: number } {
  if (dir && dir.team !== null && dir.team === chassis.team) {
    return { motor: DIRECTOR_CAR_MOTOR, aero: DIRECTOR_CAR_AERO };
  }
  return { motor: 0, aero: 0 };
}

/** Flat pace added to a driver before the track's pace weight (strategist). */
export function directorPaceBonus(dir: Director | null): number {
  return dir?.signature.kind === "strategist" ? dir.signature.paceBonus : 0;
}

/** Rain skill added in the wet only (rainMaster). Inert in the dry. */
export function directorWetBonus(dir: Director | null): number {
  return dir?.signature.kind === "rainMaster" ? dir.signature.wetBonus : 0;
}

/**
 * Flat base-score bonus, applied undiluted by the track's pace weight (unlike a
 * strategist's pace, which the track scales down). The gambler's reward side and
 * the qualifier's grid-position edge share this channel — both add a constant to
 * every race, so they matter MOST on low-pace circuits where a strategist's pace
 * barely registers.
 */
export function directorScoreBonus(dir: Director | null): number {
  if (!dir) return 0;
  if (dir.signature.kind === "gambler") return dir.signature.scoreBonus;
  if (dir.signature.kind === "qualifier") return dir.signature.gridBonus;
  return 0;
}

/**
 * Multiplier applied to the team's EXISTING synergy (motivator always; underdog
 * only when the roster is cheap — at most `maxMythics` mythic cards across the
 * two drivers and the chassis). 1× otherwise, so it never invents synergy that
 * wasn't there.
 */
export function directorSynergyMult(dir: Director | null, team: Team): number {
  if (!dir) return 1;
  if (dir.signature.kind === "motivator") return dir.signature.synergyMult;
  if (dir.signature.kind === "underdog") {
    const mythics = [team.d1, team.d2, team.chassis].filter((c) => c.rarity === "mythic").length;
    return mythics <= dir.signature.maxMythics ? dir.signature.synergyMult : 1;
  }
  return 1;
}

/**
 * The development bonus a director reaches by the final race. The `developer`
 * signature sets it (`rampMax`); every other director uses the smaller generic
 * `devSlope`. Single source of truth so the UI and the engine never disagree.
 */
export function directorDevelopmentPeak(dir: Director | null): number {
  if (!dir) return 0;
  return dir.signature.kind === "developer" ? dir.signature.rampMax : dir.devSlope;
}

/**
 * Score added by season-long development, ramping linearly from 0 at the first
 * race to its peak (see directorDevelopmentPeak) at the last.
 */
export function directorDevelopmentBonus(
  dir: Director | null,
  raceIndex: number,
  totalRaces: number,
): number {
  if (!dir) return 0;
  const progress = totalRaces > 1 ? Math.min(1, Math.max(0, raceIndex / (totalRaces - 1))) : 0;
  return directorDevelopmentPeak(dir) * progress;
}

/**
 * Multiplier on the race's luck swing. The gambler widens it; reliability
 * narrows it (and a NEGATIVE reliability — an aggressive director — widens it).
 * Reliability's effect is gentle: +90 ≈ −30% swing, −10 ≈ +3% swing.
 */
export function directorLuckMult(dir: Director | null): number {
  if (!dir) return 1;
  const gamble = dir.signature.kind === "gambler" ? dir.signature.luckMult : 1;
  const relFactor = 1 - dir.reliability / 300;
  return gamble * relFactor;
}

// How much of the weather's extra DNF pool a rain master cancels. The heavy-rain
// pool is 0.18 and light 0.08, so this trims ~40% of the heavy risk (and most of
// the light) without immunising the car — the rain master stays on track better
// than most, but the wet still bites. A full-immunity cut (0.15) paired with a
// large wetBonus turned a near-bottom McLaren into a ~20%-win auto-pick; 0.07
// keeps the rain master a contender, not a lock.
export const RAINMASTER_WETCUT = 0.07;

/**
 * DNF adjustments. The engineer's `dnfCut` lands TWICE: as an all-weather flat
 * reduction (so it is no longer dead in the 60% of races that are dry) and AGAIN
 * on the weather's extra DNF pool (`wetCut`), keeping the "especially in the wet"
 * identity. The rain master also cuts the wet pool (its cars stay on track when
 * others spin off), but only in the wet — no all-weather flat term. `flat` also
 * carries general reliability (negative for aggressive directors, so it raises
 * the risk). Both are subtracted from the final DNF probability; `wetCut` is
 * subtracted from the wet pool first.
 */
export function directorDnfDelta(dir: Director | null): { wetCut: number; flat: number } {
  if (!dir) return { wetCut: 0, flat: 0 };
  const engineerCut = dir.signature.kind === "engineer" ? dir.signature.dnfCut : 0;
  const rainCut = dir.signature.kind === "rainMaster" ? RAINMASTER_WETCUT : 0;
  return { wetCut: engineerCut + rainCut, flat: dir.reliability / 1000 + engineerCut };
}
